import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { ApiError } from '../api/client';
import { useRegister } from '../api/queries';
import { readLastWorld, rememberLastWorld } from '../api/types';
import { WorldSelect } from '../components/WorldSelect';

export function RegisterPage() {
  const register = useRegister();
  const [worldKey, setWorldKey] = useState(readLastWorld);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');

  const error = register.error instanceof ApiError ? register.error : null;
  const fieldError = (field: string): string | undefined => error?.fields[field] ?? (error?.field === field ? error.message : undefined);
  const generalError = error && !error.field ? error.message : undefined;

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    register.mutate(
      { worldKey, username, password, passwordConfirm },
      { onSuccess: () => rememberLastWorld(worldKey) },
    );
  };

  if (register.isSuccess) {
    return (
      <section className="card card-narrow center">
        <p className="big-icon" aria-hidden="true">
          ⏳
        </p>
        <h1>Registrierung eingegangen</h1>
        <p className="muted">
          Dein Konto <strong>{register.data.user.username}</strong> für die Welt{' '}
          <strong>{register.data.user.world?.name ?? '—'}</strong> wurde angelegt und wartet auf die
          Freischaltung durch einen Administrator. Danach kannst du dich anmelden.
        </p>
        <Link className="btn" to="/login">
          Zur Anmeldung
        </Link>
      </section>
    );
  }

  return (
    <section className="card card-narrow">
      <h1>Registrieren</h1>
      <p className="muted">
        Ein Konto gilt für genau eine Welt. Nach der Registrierung ist es zunächst gesperrt; ein
        Administrator schaltet es von Hand frei.
      </p>

      {generalError && <div className="flash flash-error">{generalError}</div>}

      <form className="form" onSubmit={handleSubmit} noValidate>
        <WorldSelect
          purpose="register"
          value={worldKey}
          onChange={setWorldKey}
          error={fieldError('worldKey')}
          disabled={register.isPending}
        />

        <label className="field">
          <span>Benutzername</span>
          <input
            type="text"
            name="username"
            autoComplete="username"
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            required
          />
          <small className="hint">
            3–32 Zeichen: Buchstaben, Zahlen, Punkt, Bindestrich, Unterstrich.
          </small>
          {fieldError('username') && <small className="field-error">{fieldError('username')}</small>}
        </label>

        <label className="field">
          <span>Passwort</span>
          <input
            type="password"
            name="password"
            autoComplete="new-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
          />
          <small className="hint">Mindestens 10 Zeichen.</small>
          {fieldError('password') && <small className="field-error">{fieldError('password')}</small>}
        </label>

        <label className="field">
          <span>Passwort wiederholen</span>
          <input
            type="password"
            name="passwordConfirm"
            autoComplete="new-password"
            value={passwordConfirm}
            onChange={(event) => setPasswordConfirm(event.target.value)}
            required
          />
          {fieldError('passwordConfirm') && (
            <small className="field-error">{fieldError('passwordConfirm')}</small>
          )}
        </label>

        <button type="submit" className="btn btn-primary btn-block" disabled={register.isPending}>
          {register.isPending ? 'Konto wird angelegt …' : 'Konto anlegen'}
        </button>
      </form>

      <p className="muted center">
        Bereits registriert? <Link to="/login">Zur Anmeldung</Link>
      </p>
    </section>
  );
}
